import { createContext, useContext } from 'react';
import type { RefetchConnection } from '../useMailboxConnection';

/**
 * The app-wide mailbox connect, as every button that starts one sees it (F20-i spec FR-17, FR-19; WI-1.3
 * plan Approach item 3, D-10, D-12, D-26). `MailboxConnectProvider` supplies it once, around the app frame,
 * so a failure from the automatic connect or from any interactive start lands in one place and is shown the
 * same way (F20-i Wave-1 contract § Redirect Contract, "Stub path").
 */

/** A recorded connect failure: the `mailboxConnectReason` (or `null`), shown until it is cleared. */
export interface MailboxConnectFailure {
    readonly reason: string | null;
}

export interface MailboxConnectValue {
    /**
     * The failure on show, or `null`. Kept until *Dismiss*, a successful connect, a back/forward-cache restore
     * or a page load (F20-i WI-1.3 plan D-11); a *Try again* that fails replaces it.
     */
    readonly failure: MailboxConnectFailure | null;
    /**
     * The banner's status line — and only it — is held empty: an interactive start is running on the stub path,
     * or one was refused. The visible banner keeps showing the failure's message throughout. While a start runs
     * the failure on show stays, so the banner, its message and its focused button stay, and the result is
     * announced again even when it repeats the message. After a refusal from a page's button (`NextStepAction`,
     * the sync indicator's *Reconnect*) that button's own panel says the start could not begin, and the old
     * failure is not read out again beside it; a refusal of the banner's own *Try again* is not announced — the
     * banner keeps its message and its button — which only the stub path can produce (`pnpm dev` and Vitest).
     * It ends when the failure changes.
     */
    readonly statusQuiet: boolean;
    /**
     * Whether a `CONNECT_NOW` on screen is about to start the automatic connect, or is running it (F20-i
     * WI-1.3 plan D-26; the lead's decision of 2026-11-02 in its `## Notes`): an arm — the load's first check,
     * or the Email screen's mount — waits for its check, or the stub start runs. Otherwise the start is spent
     * (it ran; the load carried a marker, F20-i WI-1.3 plan D-7; or `location.assign` was called) or will not
     * run on this check (a `CONNECT_NOW` from any other re-read), and the page's button offers *Try again*, so
     * the user is never left with no action until a reload.
     */
    readonly automaticPending: boolean;
    /**
     * Called by the Email screen as it mounts: the check its mount reads may start the automatic connect
     * (the F20-i Wave-1 contract's "When the client calls the check", F20-i AC-60). Returns the call that
     * clears that arm, for the screen's unmount.
     */
    readonly armAutomaticConnect: () => () => void;
    /**
     * Start the interactive connect (*Try again*, *Reconnect*). On the stub path it re-reads the check
     * through `refetch` (the caller's focus-restoring one, BUG-026) or the provider's own, and records a
     * `409` as the failure; it **rejects** when the start was refused for any other reason, so the caller can
     * show its own "couldn't start" panel. On the redirect path it navigates the whole page.
     */
    readonly startInteractive: (refetch?: RefetchConnection) => Promise<void>;
    /** *Dismiss*: forget the failure on show. */
    readonly dismissFailure: () => void;
    /**
     * Called by `NextStepAction` while it renders a button; returns the call that undoes it. While any is
     * registered the banner leaves out its own *Try again*, so the page's button is that page's *Try again*
     * (F20-i WI-1.3 plan D-10, the lead's two-button rule).
     */
    readonly registerNextStepButton: () => () => void;
    /** Whether a `NextStepAction` button is on screen (`registerNextStepButton`). */
    readonly nextStepButtonShown: boolean;
}

export const MailboxConnectContext = createContext<MailboxConnectValue | null>(null);

/**
 * The connect, for a component that needs it to work. Throws outside the provider: every CRM page is inside
 * it (`src/app/router.tsx`), and a button that silently did nothing would be the worse failure.
 */
export function useMailboxConnect(): MailboxConnectValue {
    const value = useContext(MailboxConnectContext);
    if (value === null) {
        throw new Error('useMailboxConnect is used outside MailboxConnectProvider');
    }
    return value;
}

/** The connect, or `null` outside the provider — for the banner, which then renders nothing (F20-i WI-1.3 plan D-4). */
export function useOptionalMailboxConnect(): MailboxConnectValue | null {
    return useContext(MailboxConnectContext);
}
